<script setup lang="ts">
import { useSubmitApplicationState } from '@/composables/current-student-advanced-standing-form/useSubmitApplicationState'
import { submitStudentForm } from '@/composables/current-student-advanced-standing-form/useStudentFormApi'
import {
  buildStudentFormSubmitRequest,
  submittedIncidentReference,
} from '@/models/current-student-advanced-standing-form/appSession'
import type { PublicFormApiResponse } from '@/models/current-student-advanced-standing-form/api'
import SubmissionSection from './SubmissionSection.vue'

const props = defineProps<{
  secureData: string
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
}>()

const emit = defineEmits<{
  'update-deploy-user': [response: PublicFormApiResponse<unknown>]
  'application-complete-changed': [complete: boolean]
  submitted: [referenceCode: string]
}>()

const {
  agreementChecked,
  buttonText,
  errorMessage,
  progressVisible,
  progressMessage,
  applicationComplete,
  submittedReferenceCode,
  progressClass,
  buttonDisabled,
  startSubmit,
  completeSubmit,
  failSubmit,
} = useSubmitApplicationState()

async function submitApplication(): Promise<void> {
  if (buttonDisabled.value) return

  startSubmit()

  try {
    const result = await submitStudentForm(buildStudentFormSubmitRequest(props.secureData))
    emit('update-deploy-user', result)

    const referenceCode = submittedIncidentReference(result.data)
    completeSubmit(referenceCode)
    emit('application-complete-changed', applicationComplete.value)
    emit('submitted', referenceCode)
  } catch (error) {
    if (props.handleAuthSessionError(error, 'redirect')) return

    failSubmit(props.getErrorMessage(error))
  }
}

</script>

<template>
  <SubmissionSection
    v-model:agreement-checked="agreementChecked"
    :application-complete="applicationComplete"
    :progress-visible="progressVisible"
    :progress-class="progressClass"
    :progress-message="progressMessage"
    :error-message="errorMessage"
    :button-text="buttonText"
    :button-disabled="buttonDisabled"
    :reference-code="submittedReferenceCode"
    @submit="submitApplication"
  />
</template>
